
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Risk } from "@/types";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface RiskMatrixProps {
  risks: Risk[];
}

export default function RiskMatrix({ risks }: RiskMatrixProps) {
  const isMobile = useIsMobile();
  const [hoveredRisk, setHoveredRisk] = useState<Risk | null>(null);

  // Define risk levels and their colors
  const riskLevels = [
    { level: "low", color: "risk-level-low", range: [1, 3], label: "Low" },
    { level: "medium", color: "risk-level-medium", range: [4, 9], label: "Medium" },
    { level: "high", color: "risk-level-high", range: [10, 15], label: "High" },
    { level: "critical", color: "risk-level-critical", range: [16, 25], label: "Critical" },
  ];

  // Get risk level color based on score
  const getRiskLevelColor = (probability: number, impact: number) => {
    const score = probability * impact;
    const riskLevel = riskLevels.find(
      (level) => score >= level.range[0] && score <= level.range[1]
    );
    return riskLevel ? riskLevel.color : "";
  };

  // Count risks in each cell
  const getRisksInCell = (probability: number, impact: number) => {
    return risks.filter(
      (risk) => risk.probability === probability && risk.impact === impact
    );
  };

  // For mobile screens, provide a simplified list view of risks
  if (isMobile) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-base sm:text-lg">Risk Analysis</CardTitle>
        </CardHeader>
        <CardContent className="overflow-y-auto max-h-[50vh] px-3 sm:px-6">
          <div className="space-y-3">
            {riskLevels.reverse().map((level) => {
              // Get risks for this level
              const levelRisks = risks.filter((risk) => {
                const score = risk.probability * risk.impact;
                return score >= level.range[0] && score <= level.range[1];
              });
              
              return (
                <div key={level.level} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded ${level.color}`}></div>
                    <h3 className="text-sm sm:text-base font-medium">{level.label} Risks ({levelRisks.length})</h3>
                  </div>
                  
                  {levelRisks.length > 0 ? (
                    <div className="pl-4 sm:pl-6 space-y-1">
                      {levelRisks.map((risk) => (
                        <div key={risk.id} className="text-xs sm:text-sm py-1 border-b border-gray-100 last:border-b-0">
                          <div className="font-medium truncate">{risk.name}</div>
                          <div className="text-xs text-muted-foreground">
                            Score: {risk.probability * risk.impact} (P:{risk.probability}, I:{risk.impact})
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="pl-4 sm:pl-6 text-xs sm:text-sm text-muted-foreground">No risks in this category</div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Desktop view - full matrix
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Risk Matrix</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <div className="risk-matrix-container" style={{ minWidth: "500px" }}>
          {/* Corner cell */}
          <div className="corner-cell"></div>
          
          {/* Probability heading */}
          <div className="probability-heading">Probability</div>
          
          {/* Impact label */}
          <div className="impact-label">Impact</div>
          
          {/* Column headers */}
          {[1, 2, 3, 4, 5].map((value) => (
            <div key={`col-${value}`} className="col-header" style={{ gridColumn: value + 1 }}>
              {value}
            </div>
          ))}
          
          {/* Row headers and matrix cells */}
          {[5, 4, 3, 2, 1].map((impact) => (
            <React.Fragment key={`row-${impact}`}>
              <div 
                className="row-header" 
                style={{ gridRow: 8 - impact }}
              >
                {impact}
              </div>
              
              {/* Matrix cells for this row */}
              {[1, 2, 3, 4, 5].map((probability) => {
                const cellRisks = getRisksInCell(probability, impact);
                const riskColor = getRiskLevelColor(probability, impact);
                
                return (
                  <div
                    key={`cell-${impact}-${probability}`}
                    className={`matrix-cell ${riskColor}`}
                    style={{ 
                      gridRow: 8 - impact,
                      gridColumn: probability + 1,
                    }}
                  >
                    {cellRisks.length > 0 ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center justify-center h-full w-full">
                              <Badge variant="secondary" className="cursor-pointer text-xs">
                                {cellRisks.length}
                              </Badge>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs p-2 z-50">
                            <div className="text-sm font-medium mb-1">
                              Risks in this category:
                            </div>
                            <ul className="text-xs space-y-1">
                              {cellRisks.map((risk) => (
                                <li key={risk.id}>
                                  {risk.name} (R-{risk.id})
                                </li>
                              ))}
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="text-xs sm:text-sm">{probability * impact}</span>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
          
          {/* Legend */}
          <div className="matrix-legend">
            {riskLevels.map((level) => (
              <div key={level.level} className="legend-item">
                <span className={`legend-color ${level.color}`}></span>
                <span className="text-xs sm:text-sm">{level.label}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
